import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { timeEntryAPI, billingRateAPI, monthlyTimesheetAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { UserTimeEntry, BillingRatePublic, WeeklySummary, MonthlyTimesheetDetail, MonthlyTimesheet } from '../types';
import api from '../services/api';

// Helper function to format date as YYYY-MM-DD in local timezone
const formatDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Monday of the week for the given date (matches backend week boundary)
const getWeekStartKey = (date: Date): string => {
  const d = new Date(date);
  const daysSinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - daysSinceMonday);
  return formatDateString(d);
};

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
}

const TimeEntry: React.FC = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [myRates, setMyRates] = useState<BillingRatePublic[]>([]);
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredRateId, setHoveredRateId] = useState<number | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<number | null>(null);
  
  // Admin: User selection
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  
  // Monthly timesheet status
  const [currentTimesheet, setCurrentTimesheet] = useState<MonthlyTimesheetDetail | null>(null);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  
  // Timesheet notifications (pending approval & rejected)
  const [pendingTimesheets, setPendingTimesheets] = useState<MonthlyTimesheet[]>([]);
  const [rejectedTimesheets, setRejectedTimesheets] = useState<MonthlyTimesheet[]>([]);
  
  // Last month warning (no entries or needs submission)
  const [lastMonthWarning, setLastMonthWarning] = useState<{
    type: 'no_entries' | 'needs_submission';
    year: number;
    month: number;
    timesheet?: MonthlyTimesheet;
  } | null>(null);
  
  const [formData, setFormData] = useState({
    billing_rate: '',
    hours: '',
    notes: '',
  });
  
  // Track entry being edited (null = creating new entry)
  const [editingEntry, setEditingEntry] = useState<UserTimeEntry | null>(null);

  // Narrow viewport (e.g. mobile): single-column layout and by-day cards instead of table
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = () => setIsNarrowViewport(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Fetch users for admin selector
  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users/');
      setUsers(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Fetch billing rates for the specified user (or current user if no userId)
  const fetchMyRates = useCallback(async (userId?: number) => {
    try {
      const response = await billingRateAPI.getMyRates(userId);
      setMyRates(response.data);
    } catch (error) {
      console.error('Failed to fetch billing rates:', error);
    }
  }, []);

  // Fetch weekly summary for the given week (weekOf = Monday YYYY-MM-DD)
  const fetchWeeklySummary = useCallback(async (weekOf: string) => {
    try {
      setLoading(true);
      const url = (isAdmin || isSuperAdmin) && selectedUserId
        ? `/time-entries/weekly_summary/?week_of=${weekOf}&user_id=${selectedUserId}`
        : `/time-entries/weekly_summary/?week_of=${weekOf}`;
      const response = await api.get(url);
      setWeeklySummary(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly summary:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isSuperAdmin, selectedUserId]);

  // Week key (Monday YYYY-MM-DD) for current selected date - only changes when week changes
  const weekStartKey = useMemo(() => getWeekStartKey(selectedDate), [selectedDate]);

  // Fetch monthly timesheet for current month
  const fetchMonthlyTimesheet = useCallback(async () => {
    if (!selectedUserId) return;
    
    try {
      setTimesheetLoading(true);
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const response = await monthlyTimesheetAPI.getForMonth(year, month, 
        (isAdmin || isSuperAdmin) ? selectedUserId : undefined
      );
      setCurrentTimesheet(response.data);
    } catch (error) {
      console.error('Failed to fetch monthly timesheet:', error);
      setCurrentTimesheet(null);
    } finally {
      setTimesheetLoading(false);
    }
  }, [selectedDate, selectedUserId, isAdmin, isSuperAdmin]);

  // Fetch pending and rejected timesheets for notifications
  const fetchTimesheetNotifications = useCallback(async () => {
    if (!selectedUserId) return;
    
    try {
      // Get all timesheets for the user
      const response = await monthlyTimesheetAPI.getAll({
        user_id: (isAdmin || isSuperAdmin) ? selectedUserId : undefined
      });
      
      const timesheets = response.data.results || response.data || [];
      
      // Filter for submitted (pending approval) timesheets
      const pending = timesheets.filter((ts: MonthlyTimesheet) => ts.status === 'submitted');
      
      // Filter for rejected timesheets (draft status with rejection reason)
      const rejected = timesheets.filter(
        (ts: MonthlyTimesheet) => ts.status === 'draft' && ts.rejection_reason && ts.rejection_reason.trim() !== ''
      );
      
      setPendingTimesheets(pending);
      setRejectedTimesheets(rejected);
      
      // Check last month's timesheet status for warnings
      const now = new Date();
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthYear = lastMonthDate.getFullYear();
      const lastMonthNum = lastMonthDate.getMonth() + 1; // Convert to 1-indexed for database comparison
      
      // Find last month's timesheet in the fetched list
      const lastMonthTimesheet = timesheets.find((ts: MonthlyTimesheet) => 
        ts.year === lastMonthYear && ts.month === lastMonthNum
      );
      
      // Determine warning type
      const entriesCount = lastMonthTimesheet?.entries_count ?? 0;
      if (!lastMonthTimesheet || entriesCount === 0) {
        // Scenario 1: No time entered at all for last month
        setLastMonthWarning({
          type: 'no_entries',
          year: lastMonthYear,
          month: lastMonthNum,
          timesheet: lastMonthTimesheet || undefined
        });
      } else if (
        lastMonthTimesheet.status === 'draft' &&
        entriesCount > 0 &&
        (!lastMonthTimesheet.rejection_reason || lastMonthTimesheet.rejection_reason.trim() === '')
      ) {
        // Scenario 2: Has entries but not submitted (and not rejected)
        setLastMonthWarning({
          type: 'needs_submission',
          year: lastMonthYear,
          month: lastMonthNum,
          timesheet: lastMonthTimesheet
        });
      } else {
        // No warning needed (already submitted, approved, or was rejected)
        setLastMonthWarning(null);
      }
    } catch (error) {
      console.error('Failed to fetch timesheet notifications:', error);
      setPendingTimesheets([]);
      setRejectedTimesheets([]);
      setLastMonthWarning(null);
    }
  }, [selectedUserId, isAdmin, isSuperAdmin]);

  useEffect(() => {
    if (isAdmin || isSuperAdmin) {
      fetchUsers();
    }
  }, [isAdmin, isSuperAdmin]);

  // Fetch billing rates when selectedUserId changes
  useEffect(() => {
    if (selectedUserId !== null) {
      // For admins, pass the selectedUserId; for regular users, pass undefined (uses their own)
      const userIdToFetch = (isAdmin || isSuperAdmin) ? selectedUserId : undefined;
      fetchMyRates(userIdToFetch);
    }
  }, [selectedUserId, isAdmin, isSuperAdmin, fetchMyRates]);
  
  // Initialize selectedUserId when user loads
  useEffect(() => {
    if (user && selectedUserId === null) {
      setSelectedUserId(user.id);
    }
  }, [user, selectedUserId]);

  // Fetch weekly summary when week or user changes (not when only the day within the same week changes)
  useEffect(() => {
    if (selectedUserId !== null) {
      fetchWeeklySummary(weekStartKey);
    }
  }, [selectedUserId, weekStartKey, fetchWeeklySummary]);
  
  // Clear edit mode and form only when switching users (not when date changes)
  useEffect(() => {
    if (selectedUserId !== null) {
      setSelectedRateId(null);
      setEditingEntry(null);
      setFormData({ billing_rate: '', hours: '', notes: '' });
    }
  }, [selectedUserId]);

  // Fetch monthly timesheet when date or user changes
  useEffect(() => {
    if (selectedUserId !== null) {
      fetchMonthlyTimesheet();
    }
  }, [selectedUserId, fetchMonthlyTimesheet]);

  // Fetch timesheet notifications when user changes
  useEffect(() => {
    if (selectedUserId !== null) {
      fetchTimesheetNotifications();
    }
  }, [selectedUserId, fetchTimesheetNotifications]);

  const handleSubmitTimesheet = async () => {
    if (!currentTimesheet) return;
    // Review & submit on My Timesheets for the timesheet month
    const params = new URLSearchParams({
      year: String(currentTimesheet.year),
      month: String(currentTimesheet.month),
    });
    // When admin is entering for another user, open that user's review page
    if (
      (isAdmin || isSuperAdmin) &&
      selectedUserId != null &&
      user?.id != null &&
      selectedUserId !== user.id
    ) {
      params.set('user_id', String(selectedUserId));
    }
    navigate(`/my-timesheets?${params.toString()}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.billing_rate || !formData.hours) {
      alert('Please select a rate and enter hours');
      return;
    }

    const dateStr = formatDateString(selectedDate);

    // If we're editing an existing entry, update it
    if (editingEntry) {
      try {
        await timeEntryAPI.update(editingEntry.id!, {
          billing_rate: parseInt(formData.billing_rate),
          date: dateStr,
          hours: parseFloat(formData.hours),
          notes: formData.notes,
        });
        
        // Clear edit mode and reset form
        handleCancelEdit();
        fetchWeeklySummary(weekStartKey);
        fetchMonthlyTimesheet();
      } catch (error: any) {
        console.error('Failed to update time entry:', error);
        const errorMsg = error.response?.data?.non_field_errors?.[0] ||
                         error.response?.data?.billing_rate?.[0] || 
                         error.response?.data?.detail || 
                         error.response?.data?.error ||
                         'Failed to update time entry';
        alert(errorMsg);
      }
      return;
    }

    // Creating a new entry - check if entry already exists for this rate/date
    const existingEntry = weeklySummary?.entries.find(
      (entry) => entry.billing_rate === parseInt(formData.billing_rate) && entry.date === dateStr
    );

    if (existingEntry) {
      const confirmed = window.confirm(
        `You already have ${existingEntry.hours} hours logged for this rate on this date. ` +
        `Would you like to update it to ${formData.hours} hours instead?`
      );
      
      if (confirmed) {
        try {
          await timeEntryAPI.update(existingEntry.id!, {
            billing_rate: parseInt(formData.billing_rate),
            date: dateStr,
            hours: parseFloat(formData.hours),
            notes: formData.notes,
          });
          
          // Reset form (keep rate selected) and refresh summary
          setFormData((prev) => ({ ...prev, hours: '', notes: '' }));
          fetchWeeklySummary(weekStartKey);
          fetchMonthlyTimesheet(); // Refresh timesheet to update totals
        } catch (error: any) {
          console.error('Failed to update time entry:', error);
          const errorMsg = error.response?.data?.non_field_errors?.[0] ||
                           error.response?.data?.billing_rate?.[0] || 
                           error.response?.data?.detail || 
                           error.response?.data?.error ||
                           'Failed to update time entry';
          alert(errorMsg);
        }
      }
      return;
    }

    try {
      const createPayload: Parameters<typeof timeEntryAPI.create>[0] = {
        billing_rate: parseInt(formData.billing_rate),
        date: formatDateString(selectedDate),
        hours: parseFloat(formData.hours),
        notes: formData.notes,
      };
      if ((isAdmin || isSuperAdmin) && selectedUserId !== null) {
        createPayload.user = selectedUserId;
      }
      await timeEntryAPI.create(createPayload);
      
      // Reset form (keep rate selected) and refresh summary
      setFormData((prev) => ({ ...prev, hours: '', notes: '' }));
      fetchWeeklySummary(weekStartKey);
      fetchMonthlyTimesheet(); // Refresh timesheet to update totals
    } catch (error: any) {
      console.error('Failed to save time entry:', error);
      const errorMsg = error.response?.data?.non_field_errors?.[0] ||
                       error.response?.data?.billing_rate?.[0] || 
                       error.response?.data?.detail || 
                       error.response?.data?.error ||
                       'Failed to save time entry';
      alert(errorMsg);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDelete = async (id: number) => {
    if (!currentTimesheet?.can_edit) {
      alert('Cannot delete entries - timesheet is locked for this month');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        await timeEntryAPI.delete(id);
        fetchWeeklySummary(weekStartKey);
        fetchMonthlyTimesheet(); // Refresh timesheet to update totals
        // Clear edit mode if we deleted the entry being edited
        if (editingEntry?.id === id) {
          handleCancelEdit();
        }
      } catch (error) {
        console.error('Failed to delete time entry:', error);
        alert('Failed to delete time entry');
      }
    }
  };

  // Handle clicking on an entry to edit it
  const handleEditEntry = (entry: UserTimeEntry) => {
    if (!currentTimesheet?.can_edit) {
      alert('Cannot edit entries - timesheet is locked for this month');
      return;
    }
    
    // Parse the entry's date and set it as selected
    const [year, month, day] = entry.date.split('-').map(Number);
    setSelectedDate(new Date(year, month - 1, day));
    
    // Populate form with entry data
    setFormData({
      billing_rate: entry.billing_rate.toString(),
      hours: entry.hours.toString(),
      notes: entry.notes || '',
    });
    
    // Set editing mode
    setEditingEntry(entry);
  };

  // Cancel editing and reset form
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setFormData({
      billing_rate: '',
      hours: '',
      notes: '',
    });
  };

  const goToPreviousWeek = () => {
    setSelectedDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const goToNextWeek = () => {
    setSelectedDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  const getWeekDates = () => {
    if (!weeklySummary) return [];
    // Parse date string manually to avoid timezone issues
    const [year, month, day] = weeklySummary.week_start.split('-').map(Number);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(year, month - 1, day + i);
      dates.push(date);
    }
    return dates;
  };

  const getEntryForRateAndDate = (rateId: number, date: Date) => {
    if (!weeklySummary) return null;
    const dateStr = formatDateString(date);
    return weeklySummary.entries.find(
      (entry) => entry.billing_rate === rateId && entry.date === dateStr
    );
  };

  const getDailyTotal = (date: Date) => {
    if (!weeklySummary) return 0;
    const dateStr = formatDateString(date);
    return weeklySummary.entries
      .filter((entry) => entry.date === dateStr)
      .reduce((sum, entry) => sum + parseFloat(entry.hours.toString()), 0);
  };

  const getRateTotal = (rateId: number) => {
    if (!weeklySummary) return 0;
    return weeklySummary.entries
      .filter((entry) => entry.billing_rate === rateId)
      .reduce((sum, entry) => sum + parseFloat(entry.hours.toString()), 0);
  };

  const getNotesForRate = (rateId: number) => {
    if (!weeklySummary) return [];
    return weeklySummary.entries
      .filter((entry) => entry.billing_rate === rateId)
      .map((entry) => ({
        date: entry.date,
        notes: entry.notes || null,
        hours: entry.hours,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const formatDateForDisplay = (dateString: string): string => {
    // Parse YYYY-MM-DD in local timezone
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const weekDates = getWeekDates();
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  if (myRates.length === 0 && !loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Time Entry</h2>
          <p style={{ color: '#e74c3c', marginTop: '1rem' }}>
            You have no billing rates assigned. Please contact your administrator to get assigned to a project.
          </p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return '#3498db';
      case 'submitted': return '#f39c12';
      case 'approved': return '#27ae60';
      case 'invoiced': return '#95a5a6';
      default: return '#7f8c8d';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Submitted - Pending Approval';
      case 'approved': return 'Approved';
      case 'invoiced': return 'Invoiced';
      default: return status;
    }
  };

  const goToReviewMonth = (year: number, month: number) => {
    const params = new URLSearchParams({
      year: String(year),
      month: String(month),
    });
    if (
      (isAdmin || isSuperAdmin) &&
      selectedUserId != null &&
      user?.id != null &&
      selectedUserId !== user.id
    ) {
      params.set('user_id', String(selectedUserId));
    }
    navigate(`/my-timesheets?${params.toString()}`);
  };

  const goToEditMonth = (year: number, month: number) => {
    // Stay on Time Entry so the user can add/edit entries for that month
    setSelectedDate(new Date(year, month - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="container">
      <h2>Time Entry</h2>
      
      {/* Timesheet Notifications (Pending & Rejected) */}
      {(pendingTimesheets.length > 0 || rejectedTimesheets.length > 0) && (
        <div style={{ marginBottom: '1.5rem' }}>
          {/* Pending Approvals Notifications */}
          {pendingTimesheets.length > 0 && (
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              backgroundColor: '#fff9c4',
              border: '2px solid #fbc02d',
              borderRadius: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⏳</span>
                <h3 style={{ margin: 0, color: '#f57c00' }}>
                  {pendingTimesheets.length} Timesheet{pendingTimesheets.length > 1 ? 's' : ''} Pending Approval
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {pendingTimesheets.map(ts => (
                  <div 
                    key={ts.id} 
                    style={{ 
                      padding: '0.75rem', 
                      backgroundColor: 'white', 
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}
                  >
                    <div>
                      <strong>{new Date(ts.year, ts.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
                      {' - '}
                      <span>{ts.total_hours} hours ({ts.entries_count} entries)</span>
                      {ts.submitted_at && (
                        <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: '0.25rem' }}>
                          Submitted: {new Date(ts.submitted_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => goToReviewMonth(ts.year, ts.month)}
                      className="btn btn-primary"
                      style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                    >
                      View Month
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Rejected Timesheets Notifications */}
          {rejectedTimesheets.length > 0 && (
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              backgroundColor: '#ffebee',
              border: '2px solid #ef5350',
              borderRadius: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>❌</span>
                <h3 style={{ margin: 0, color: '#c62828' }}>
                  {rejectedTimesheets.length} Timesheet{rejectedTimesheets.length > 1 ? 's' : ''} Rejected
                </h3>
              </div>
              <p style={{ margin: '0 0 0.75rem 0', color: '#c62828' }}>
                These timesheets have been rejected and need to be corrected and resubmitted.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {rejectedTimesheets.map(ts => (
                  <div 
                    key={ts.id} 
                    style={{ 
                      padding: '0.75rem', 
                      backgroundColor: 'white', 
                      borderRadius: '4px',
                      borderLeft: '4px solid #ef5350'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div>
                        <strong>{new Date(ts.year, ts.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
                        {' - '}
                        <span>{ts.total_hours} hours ({ts.entries_count} entries)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => goToEditMonth(ts.year, ts.month)}
                        className="btn btn-primary"
                        style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                      >
                        View & Fix
                      </button>
                    </div>
                    {ts.rejection_reason && (
                      <div style={{
                        padding: '0.5rem',
                        backgroundColor: '#ffebee',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                      }}>
                        <strong>Rejection Reason:</strong> {ts.rejection_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Last Month Warning Notifications */}
      {lastMonthWarning && (
        <div style={{ marginBottom: '1.5rem' }}>
          {lastMonthWarning.type === 'no_entries' && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#ffebee',
              border: '2px solid #c62828',
              borderRadius: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>🚨</span>
                <h3 style={{ margin: 0, color: '#c62828' }}>
                  No Time Entered for {new Date(lastMonthWarning.year, lastMonthWarning.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
              </div>
              <p style={{ margin: '0 0 0.75rem 0', color: '#c62828' }}>
                You haven't recorded any time entries for last month.
              </p>
              <button
                type="button"
                onClick={() => goToEditMonth(lastMonthWarning.year, lastMonthWarning.month)}
                className="btn btn-primary"
                style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
              >
                Enter Time
              </button>
            </div>
          )}
          
          {lastMonthWarning.type === 'needs_submission' && lastMonthWarning.timesheet && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fff3e0',
              border: '2px solid #f57c00',
              borderRadius: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: '#e65100' }}>
                  {new Date(lastMonthWarning.year, lastMonthWarning.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Timesheet Not Submitted
                </h3>
              </div>
              <p style={{ margin: '0 0 0.75rem 0', color: '#e65100' }}>
                You have {lastMonthWarning.timesheet.total_hours} hours ({lastMonthWarning.timesheet.entries_count} entries) that need to be submitted for approval.
              </p>
              <button
                type="button"
                onClick={() => goToReviewMonth(lastMonthWarning.year, lastMonthWarning.month)}
                className="btn btn-primary"
                style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
              >
                View & Submit
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Monthly Timesheet Status Banner */}
      {currentTimesheet && (
        <div style={{
          padding: '1rem',
          marginBottom: '1.5rem',
          backgroundColor: currentTimesheet.can_edit ? '#e8f5e9' : '#fff3cd',
          border: `2px solid ${getStatusColor(currentTimesheet.status)}`,
          borderRadius: '8px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: getStatusColor(currentTimesheet.status) }}>
                {new Date(currentTimesheet.year, currentTimesheet.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Timesheet
              </h3>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div>
                  <strong>Status:</strong> <span style={{ color: getStatusColor(currentTimesheet.status), fontWeight: 'bold' }}>
                    {getStatusLabel(currentTimesheet.status)}
                  </span>
                </div>
                <div>
                  <strong>Total Hours:</strong> {parseFloat(currentTimesheet.total_hours || '0').toFixed(2)}
                </div>
                <div>
                  <strong>Entries:</strong> {currentTimesheet.entries_count || 0}
                </div>
              </div>
              {currentTimesheet.submitted_at && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
                  Submitted: {new Date(currentTimesheet.submitted_at).toLocaleString()}
                </div>
              )}
              {currentTimesheet.approved_at && currentTimesheet.approved_by_username && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
                  Approved by {currentTimesheet.approved_by_username} on {new Date(currentTimesheet.approved_at).toLocaleString()}
                </div>
              )}
              {currentTimesheet.rejection_reason && currentTimesheet.status === 'draft' && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#fee',
                  borderRadius: '4px',
                  color: '#c0392b'
                }}>
                  <strong>Rejected:</strong> {currentTimesheet.rejection_reason}
                </div>
              )}
              {!currentTimesheet.can_edit && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#fff3cd',
                  borderRadius: '4px',
                  color: '#856404',
                  fontWeight: 'bold'
                }}>
                  🔒 Time entries for this month cannot be edited ({currentTimesheet.status})
                </div>
              )}
            </div>
            {currentTimesheet.can_submit && currentTimesheet.can_edit && (
              <button 
                type="button"
                onClick={handleSubmitTimesheet}
                className="btn btn-primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                Review & Submit
              </button>
            )}
          </div>
        </div>
      )}
      
      <div
        className="time-entry-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowViewport ? '1fr' : '1fr 2fr',
          gap: '2rem',
        }}
      >
        {/* Left side: Form */}
        <div className="card" style={editingEntry ? { border: '2px solid #3498db', boxShadow: '0 0 10px rgba(52, 152, 219, 0.3)' } : {}}>
          <h3>{editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}</h3>
          {editingEntry && (
            <div style={{
              padding: '0.5rem',
              marginBottom: '1rem',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#1565c0'
            }}>
              Editing entry from {formatDateForDisplay(editingEntry.date)}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {(isAdmin || isSuperAdmin) && users.length > 0 && selectedUserId !== null && (
              <div className="form-group">
                <label htmlFor="user_selector">View/Enter Time For:</label>
                <select
                  id="user_selector"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(parseInt(e.target.value))}
                  style={{ 
                    backgroundColor: '#e8f5e9', 
                    border: '2px solid #4caf50',
                    fontWeight: 'bold'
                  }}
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username} {u.first_name || u.last_name ? `(${u.first_name} ${u.last_name})` : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#2e7d32', marginTop: '0.25rem', display: 'block' }}>
                  As an admin, you can view and enter time for any user
                </small>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => date && setSelectedDate(date)}
                inline
                calendarStartDay={1}
                id="date"
              />
            </div>

            <div className="form-group">
              <label htmlFor="billing_rate">Billing Rate *</label>
              <select
                id="billing_rate"
                name="billing_rate"
                value={formData.billing_rate}
                onChange={handleChange}
                required
              >
                <option value="">Select a rate</option>
                {myRates.map((rate) => (
                  <option key={rate.id} value={rate.id}>
                    {rate.project_code} - {rate.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="hours">Hours *</label>
              <input
                type="number"
                id="hours"
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                step="0.25"
                min="0.25"
                max="24"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Description of work performed..."
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={!currentTimesheet?.can_edit}
                title={!currentTimesheet?.can_edit ? 'Cannot add entries - timesheet is locked' : ''}
              >
                {editingEntry ? 'Update Entry' : 'Add Time Entry'}
              </button>
              {editingEntry && (
                <button 
                  type="button" 
                  className="btn"
                  onClick={handleCancelEdit}
                  style={{ backgroundColor: '#95a5a6', color: 'white' }}
                >
                  Cancel
                </button>
              )}
            </div>
            {!currentTimesheet?.can_edit && (
              <p style={{ color: '#c0392b', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                🔒 Time entry is locked for this month
              </p>
            )}
          </form>
        </div>

        {/* Right side: Weekly Summary Table */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn"
              onClick={goToPreviousWeek}
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.9rem' }}
              title="Previous week"
            >
              ← Prev week
            </button>
            <span style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
              Week of {weeklySummary?.week_start ?? '...'} to {weeklySummary?.week_end ?? '...'}
            </span>
            <button
              type="button"
              className="btn"
              onClick={goToNextWeek}
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.9rem' }}
              title="Next week"
            >
              Next week →
            </button>
          </div>
          <h3>Weekly Summary</h3>
          
          {loading ? (
            <p>Loading...</p>
          ) : weeklySummary && weekDates.length > 0 ? (
            isNarrowViewport ? (
              /* Mobile: by-day card/list */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {weekDates.map((date, i) => {
                  const dateStr = formatDateString(date);
                  const dayEntries = weeklySummary.entries.filter((e) => e.date === dateStr);
                  const dayTotal = getDailyTotal(date);
                  return (
                    <div key={dateStr}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#2c3e50' }}>
                        {weekDays[i]} {date.getMonth() + 1}/{date.getDate()}
                      </div>
                      {dayEntries.length === 0 ? (
                        <p style={{ margin: 0, color: '#7f8c8d', fontSize: '0.9rem', fontStyle: 'italic' }}>No entries</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {dayEntries.map((entry) => {
                            const rate = myRates.find((r) => r.id === entry.billing_rate);
                            const rateLabel = rate ? `${rate.project_code} - ${rate.name}` : `Rate #${entry.billing_rate}`;
                            const isEditing = editingEntry?.id === entry.id;
                            return (
                              <div
                                key={entry.id}
                                onClick={() => currentTimesheet?.can_edit && handleEditEntry(entry)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if ((e.key === 'Enter' || e.key === ' ') && currentTimesheet?.can_edit) {
                                    e.preventDefault();
                                    handleEditEntry(entry);
                                  }
                                }}
                                style={{
                                  padding: '0.75rem',
                                  backgroundColor: isEditing ? '#e3f2fd' : '#f8f9fa',
                                  border: isEditing ? '2px solid #3498db' : '1px solid #dee2e6',
                                  borderRadius: '8px',
                                  cursor: currentTimesheet?.can_edit ? 'pointer' : 'default',
                                  minHeight: '44px',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                                  <div>
                                    <div style={{ fontWeight: '600', color: '#2c3e50' }}>{rateLabel}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                                      {entry.hours} h
                                      {entry.notes ? ` · ${entry.notes.length > 40 ? entry.notes.slice(0, 40) + '…' : entry.notes}` : ' · No notes'}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(entry.id!);
                                    }}
                                    className="btn btn-danger"
                                    style={{ minWidth: '44px', minHeight: '44px', padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                    disabled={!currentTimesheet?.can_edit}
                                    title={!currentTimesheet?.can_edit ? 'Cannot delete - timesheet is locked' : 'Delete this entry'}
                                  >
                                    Del
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {dayEntries.length > 0 && (
                        <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginTop: '0.25rem' }}>
                          Day total: {dayTotal.toFixed(2)}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ borderTop: '2px solid #2c3e50', paddingTop: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
                  Week total: {weeklySummary.total_hours.toFixed(2)} hours
                </div>
              </div>
            ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                      Rate
                    </th>
                    {weekDates.map((date, i) => (
                      <th key={i} style={{ textAlign: 'center', minWidth: '80px' }}>
                        <div>{weekDays[i]}</div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>
                          {date.getMonth() + 1}/{date.getDate()}
                        </div>
                      </th>
                    ))}
                    <th style={{ textAlign: 'center', fontWeight: 'bold' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {myRates.map((rate) => {
                    const isHovered = hoveredRateId === rate.id;
                    const isSelected = selectedRateId === rate.id;
                    const rowBgColor = isSelected ? '#c8e6c9' : isHovered ? '#f0f8ff' : 'transparent';
                    const cellBgColor = isSelected ? '#c8e6c9' : isHovered ? '#f0f8ff' : 'white';
                    
                    return (
                    <tr 
                      key={rate.id}
                      className={isSelected ? 'rate-row-selected' : ''}
                      onMouseEnter={() => setHoveredRateId(rate.id)}
                      onMouseLeave={() => setHoveredRateId(null)}
                      onClick={() => {
                        setSelectedRateId(isSelected ? null : rate.id);
                      }}
                      style={{ 
                        backgroundColor: rowBgColor,
                        transition: 'background-color 0.2s',
                        cursor: 'pointer'
                      } as React.CSSProperties}
                    >
                      <td 
                        style={{ position: 'sticky', left: 0, backgroundColor: cellBgColor, fontWeight: 'bold', transition: 'background-color 0.2s' } as React.CSSProperties}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRateId(isSelected ? null : rate.id);
                        }}
                      >
                        <div>{rate.project_code}</div>
                        <div style={{ fontSize: '0.85rem', color: '#7f8c8d', fontWeight: 'normal' }}>
                          {rate.name}
                        </div>
                      </td>
                      {weekDates.map((date, i) => {
                        const entry = getEntryForRateAndDate(rate.id, date);
                        return (
                          <td 
                            key={i} 
                            style={{ textAlign: 'center' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRateId(isSelected ? null : rate.id);
                            }}
                          >
                            {entry ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                                <strong
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditEntry(entry);
                                  }}
                                  style={{
                                    cursor: currentTimesheet?.can_edit ? 'pointer' : 'default',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    backgroundColor: editingEntry?.id === entry.id ? '#3498db' : 'transparent',
                                    color: editingEntry?.id === entry.id ? 'white' : 'inherit',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (currentTimesheet?.can_edit && editingEntry?.id !== entry.id) {
                                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (editingEntry?.id !== entry.id) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                  title={currentTimesheet?.can_edit ? 'Click to edit this entry' : 'Cannot edit - timesheet is locked'}
                                >
                                  {entry.hours}
                                </strong>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(entry.id!);
                                  }}
                                  className="btn btn-danger btn-small"
                                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                                  disabled={!currentTimesheet?.can_edit}
                                  title={!currentTimesheet?.can_edit ? 'Cannot delete - timesheet is locked' : 'Delete this entry'}
                                >
                                  Del
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: '#ccc' }}>-</span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                        {getRateTotal(rate.id).toFixed(2)}
                      </td>
                    </tr>
                    );
                  })}
                  <tr style={{ borderTop: '2px solid #2c3e50' }}>
                    <td style={{ position: 'sticky', left: 0, backgroundColor: 'white', fontWeight: 'bold' }}>
                      Daily Total
                    </td>
                    {weekDates.map((date, i) => (
                      <td key={i} style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                        {getDailyTotal(date).toFixed(2)}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e8f4f8', fontSize: '1.1rem' }}>
                      {weeklySummary.total_hours.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            )
          ) : (
            <p>No time entries for this week.</p>
          )}

          {/* Notes Panel (desktop only) */}
          {!isNarrowViewport && selectedRateId && (
            <div style={{ 
              marginTop: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '2px solid #3498db',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, color: '#2c3e50' }}>
                  Notes for {myRates.find(r => r.id === selectedRateId)?.project_code} - {myRates.find(r => r.id === selectedRateId)?.name}
                </h4>
                <button
                  onClick={() => setSelectedRateId(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    color: '#7f8c8d',
                    cursor: 'pointer',
                    padding: '0 0.5rem',
                    lineHeight: '1',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#e74c3c'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#7f8c8d'}
                  title="Close notes panel"
                >
                  ×
                </button>
              </div>
              {(() => {
                const entries = getNotesForRate(selectedRateId);
                if (entries.length === 0) {
                  return <p style={{ margin: 0, color: '#7f8c8d', fontStyle: 'italic' }}>No time entries for this rate this week.</p>;
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {entries.map((entry, idx) => (
                      <div key={idx} style={{ 
                        padding: '0.5rem', 
                        backgroundColor: 'white', 
                        borderRadius: '4px',
                        borderLeft: entry.notes ? '3px solid #3498db' : '3px solid #f39c12'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: '0.25rem',
                          fontSize: '0.85rem',
                          color: '#7f8c8d'
                        }}>
                          <span><strong>{formatDateForDisplay(entry.date)}</strong></span>
                          <span>{entry.hours} hours</span>
                        </div>
                        {entry.notes ? (
                          <div style={{ color: '#2c3e50' }}>{entry.notes}</div>
                        ) : (
                          <div style={{ color: '#f39c12', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span>⚠️</span>
                            <span>No notes added for this entry</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeEntry;
